<template>
  <v-container fluid>
    <v-row align="center" justify="space-between" class="mb-4">
      <v-col cols="12" md="8">
        <h2 class="text-h5 font-weight-bold">
          Editar pedido {{ pedido ? pedido.numero_display : '' }}
        </h2>
      </v-col>
      <v-col cols="12" md="4" class="text-md-right">
        <v-btn text color="primary" @click="$router.back()">
          <v-icon left>mdi-arrow-left</v-icon>
          Volver
        </v-btn>
      </v-col>
    </v-row>

    <v-row v-if="loading">
      <v-col cols="12">
        <v-skeleton-loader type="article" />
      </v-col>
    </v-row>

    <v-row v-else-if="pedido">
      <!-- Panel izquierdo -->
      <v-col cols="12" md="4">
        <v-card class="mb-4">
          <v-card-title class="subtitle-1 font-weight-bold">Proveedor</v-card-title>
          <v-card-text>
            <div class="font-weight-medium">{{ pedido.proveedor.nombre }}</div>
            <div class="caption grey--text mt-1">El proveedor no se puede cambiar al editar.</div>
          </v-card-text>
        </v-card>

        <v-card class="mb-4">
          <v-card-title class="subtitle-1 font-weight-bold">Agregar producto</v-card-title>
          <v-card-text>
            <v-autocomplete
              v-model="productoSeleccionado"
              :items="productosSearch"
              :loading="loadingProductos"
              :search-input.sync="searchProducto"
              item-text="descripcion"
              outlined
              dense
              label="Buscar producto..."
              placeholder="Escriba para buscar"
              @change="agregarProductoDetalle"
              return-object
              no-filter
              clearable
              hide-no-data
            />
          </v-card-text>
        </v-card>

        <v-card>
          <v-card-title class="subtitle-1 font-weight-bold">Observaciones</v-card-title>
          <v-card-text>
            <v-textarea
              v-model="observaciones"
              outlined
              dense
              rows="3"
              placeholder="Opcional"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Panel derecho: detalles -->
      <v-col cols="12" md="8">
        <v-card>
          <v-card-title class="subtitle-1 font-weight-bold">Detalle del pedido</v-card-title>
          <v-card-text>
            <v-simple-table v-if="detalles.length > 0">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th class="text-right" style="width: 130px">Cantidad</th>
                    <th style="width: 50px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(detalle, i) in detalles" :key="i">
                    <td>{{ detalle.descripcion }}</td>
                    <td class="text-right">
                      <v-text-field
                        v-model.number="detalle.cantidad"
                        type="number"
                        min="0.01"
                        dense
                        outlined
                        hide-details
                        style="width: 100px"
                        class="ml-auto"
                      />
                    </td>
                    <td>
                      <v-btn icon small color="error" @click="quitarDetalle(i)">
                        <v-icon small>mdi-delete</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <v-alert v-else type="info" outlined dense class="mt-2">
              Sin productos. Agregue productos desde el panel izquierdo.
            </v-alert>
          </v-card-text>

          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn
              color="success"
              :disabled="detalles.length === 0"
              :loading="loadingGuardar"
              @click="guardar"
            >
              <v-icon left>mdi-content-save</v-icon>
              Guardar cambios
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapActions } from 'vuex';

export default {
  name: 'CompraPedidoEmpresaEditar',
  data() {
    return {
      pedido: null,
      loading: false,
      loadingGuardar: false,
      observaciones: '',
      detalles: [],
      productosSearch: [],
      productoSeleccionado: null,
      searchProducto: '',
      loadingProductos: false,
      _debounceTimer: null,
    };
  },
  computed: {
    pedidoId() {
      return this.$route.params.id || this.$route.params.uuid_pedido;
    },
  },
  watch: {
    searchProducto(val) {
      if (!val || val.length < 2) {
        this.productosSearch = [];
        return;
      }
      clearTimeout(this._debounceTimer);
      this._debounceTimer = setTimeout(() => this.fetchProductosSuggest(val), 300);
    },
  },
  methods: {
    ...mapActions('compraPedido', ['get_pedido_compra', 'update_pedido_compra']),

    async fetchPedido() {
      this.loading = true;
      try {
        const res = await this.get_pedido_compra(this.pedidoId);
        this.pedido = res.data;
        this.observaciones = res.data.observaciones || '';
        this.detalles = res.data.detalles.map(d => ({
          psp: d.psp,
          descripcion: d.descripcion,
          cantidad: parseFloat(d.cantidad),
        }));
      } catch (e) {
        console.error(e);
      } finally {
        this.loading = false;
      }
    },

    async fetchProductosSuggest(query) {
      this.loadingProductos = true;
      try {
        const res = await this.axios.get(`/productos/suggest?q=${encodeURIComponent(query)}`);
        this.productosSearch = res.data.results || [];
      } catch (e) {
        console.error(e);
      } finally {
        this.loadingProductos = false;
      }
    },

    agregarProductoDetalle(producto) {
      if (!producto || !producto.psp_id) return;
      const existente = this.detalles.find(d => d.psp === producto.psp_id);
      if (existente) {
        existente.cantidad = parseFloat(existente.cantidad) + 1;
      } else {
        this.detalles.push({ psp: producto.psp_id, descripcion: producto.descripcion, cantidad: 1 });
      }
      this.$nextTick(() => {
        this.productoSeleccionado = null;
        this.searchProducto = '';
        this.productosSearch = [];
      });
    },

    quitarDetalle(index) {
      this.detalles.splice(index, 1);
    },

    async guardar() {
      this.loadingGuardar = true;
      try {
        await this.update_pedido_compra({
          id: this.pedidoId,
          data: {
            observaciones: this.observaciones,
            detalles: this.detalles.map(d => ({ psp: d.psp, cantidad: String(d.cantidad) })),
          },
        });
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'success',
          text: 'Pedido actualizado', icon: 'mdi-check', timeout: 2500,
        });
        this.$router.back();
      } catch (e) {
        const msg = e.response?.data?.error || 'Error al guardar';
        this.$updateBus.$emit('showSnackBar', {
          show: true, button: false, color: 'error',
          text: msg, icon: 'mdi-alert', timeout: 3500,
        });
      } finally {
        this.loadingGuardar = false;
      }
    },
  },
  created() {
    this.fetchPedido();
  },
};
</script>
